import achData from "../data/achievements.json";
import { STAGES } from "./stages";

export interface AchievementDef {
  id: string;
  icon: string;
  /** Stardust granted on unlock. */
  reward: number;
}

export const ACHIEVEMENTS: readonly AchievementDef[] = achData as AchievementDef[];

const BY_ID = new Map<string, AchievementDef>(ACHIEVEMENTS.map((a) => [a.id, a]));
export function getAchievement(id: string): AchievementDef | undefined {
  return BY_ID.get(id);
}

/** Snapshot of values an achievement condition can read. */
export interface AchievementContext {
  maxTierReached: number;
  bestEndless: number;
  /** Total stars across all stages. */
  totalStars: number;
  /** stage id → stars. */
  clearStars: Record<string, number>;
  /** Highest chain reached in the run that just finished (optional). */
  lastChain?: number;
  /** Whether any stage has been cleared. */
  anyClear: boolean;
  /** Whether any skill has reached its max level. */
  anyMaxedSkill: boolean;
  /** Column count of the run that just finished (optional). */
  colsPlayed?: number;
  /** Consecutive-day login streak. */
  loginStreak?: number;
  /** Lifetime number of missions claimed. */
  missionsClaimedTotal?: number;
  /** Per-stage widths cleared so far. */
  clearedWidths?: Record<string, number[]>;
  /** Distinct ability ids used in the run that just finished. */
  abilitiesUsedThisRun?: string[];
  /** Whether the player dropped a wild this run. */
  usedWildThisRun?: boolean;
  /** Whether the player dropped a bomb this run. */
  usedBombThisRun?: boolean;
}

const CH1_STAGE_IDS = STAGES.filter((s) => s.world === 1 && s.chapter === 1).map((s) => s.id);
const CH2_STAGE_IDS = STAGES.filter((s) => s.world === 1 && s.chapter === 2).map((s) => s.id);
const CH3_STAGE_IDS = STAGES.filter((s) => s.world === 1 && s.chapter === 3).map((s) => s.id);
const WORLD2_STAGE_IDS = STAGES.filter((s) => s.world === 2).map((s) => s.id);

/** Conditions keyed by achievement id (pure predicates over the context). */
const CONDITIONS: Record<string, (c: AchievementContext) => boolean> = {
  first_clear: (c) => c.anyClear,
  reach_star: (c) => c.maxTierReached >= 6,
  reach_galaxy: (c) => c.maxTierReached >= 11,
  reach_cosmos: (c) => c.maxTierReached >= 12,
  chain5: (c) => (c.lastChain ?? 0) >= 5,
  chain8: (c) => (c.lastChain ?? 0) >= 8,
  chapter1: (c) => CH1_STAGE_IDS.every((id) => (c.clearStars[id] ?? 0) >= 1),
  chapter2: (c) => CH2_STAGE_IDS.length > 0 && CH2_STAGE_IDS.every((id) => (c.clearStars[id] ?? 0) >= 1),
  chapter3: (c) => CH3_STAGE_IDS.length > 0 && CH3_STAGE_IDS.every((id) => (c.clearStars[id] ?? 0) >= 1),
  world2: (c) => WORLD2_STAGE_IDS.length > 0 && WORLD2_STAGE_IDS.every((id) => (c.clearStars[id] ?? 0) >= 1),
  use_wild: (c) => !!c.usedWildThisRun,
  use_bomb: (c) => !!c.usedBombThisRun,
  all_stars_ch1: (c) => CH1_STAGE_IDS.every((id) => (c.clearStars[id] ?? 0) >= 3),
  maxskill: (c) => c.anyMaxedSkill,
  endless1000: (c) => c.bestEndless >= 1000,
  reach_blackhole: (c) => c.maxTierReached >= 9,
  chain10: (c) => (c.lastChain ?? 0) >= 10,
  endless5000: (c) => c.bestEndless >= 5000,
  wide_board: (c) => (c.colsPlayed ?? 0) >= 6,
  narrow_board: (c) => (c.colsPlayed ?? 0) > 0 && (c.colsPlayed ?? 99) <= 4,
  login_week: (c) => (c.loginStreak ?? 0) >= 7,
  login_month: (c) => (c.loginStreak ?? 0) >= 30,
  mission_master: (c) => (c.missionsClaimedTotal ?? 0) >= 20,
  all_abilities: (c) =>
    (c.abilitiesUsedThisRun ?? []).filter((a) =>
      ["hammer", "swap", "promote", "reroll"].includes(a),
    ).length >= 4,
  // Cleared every Chapter 1 stage at every unlocked width — multi-width mastery.
  ch1_polymath: (c) => {
    const widths = c.clearedWidths ?? {};
    return CH1_STAGE_IDS.every((id) => {
      const w = widths[id] ?? [];
      return w.includes(4) && w.includes(5) && w.includes(6);
    });
  },
};

/** Return the ids of achievements satisfied that are not yet in `owned`. */
export function newlyUnlocked(ctx: AchievementContext, owned: ReadonlySet<string>): string[] {
  const out: string[] = [];
  for (const def of ACHIEVEMENTS) {
    if (owned.has(def.id)) continue;
    const cond = CONDITIONS[def.id];
    if (cond && cond(ctx)) out.push(def.id);
  }
  return out;
}
